import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../utils/dateUtils";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import SaasBillingFilter from "./SaasBillingFilter";
import Pagination from "../../../Utility/Pagination/Pagination";
import Loader from "../../../Utility/Loader/Loader";
import { ContentLoader } from "../../../Utility/Loader";
import NoDataFound from "../../../Utility/NoDataFound/NoDataFound";
import { useSorting, SortableHeader } from "../../../Utility/Sorting";
import { RiUpload2Line } from "react-icons/ri";
import { fetchClientsData } from "../../../Redux/Thunks/clientsThunk";
import { fetchSaasBillingList } from "../../../Redux/Thunks/saasBillingThunk";
import {
  selectSaasBillingList,
  selectSaasBillingLoading,
  selectSaasBillingError,
} from "../../../Redux/Slices/saasBillingSlice";
import { selectClientsData } from "../../../Redux/Slices/clientsSlice";
import { fetchExportsHistory, createBillingExport } from "../../../Redux/Thunks/exportsThunk";
import { selectExportsHistoryData } from "../../../Redux/Slices/exportsSlice";
import MockResponses from "../../../Services/MockResponses.json";
import "../../Clients/clients.css";
import { usePermissions } from "../../../hooks/usePermissions";
import PermissionDeniedModal from "../../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../../permissions/permissionKeys";
import { formatExportStatusLabel } from "../../../utils/exportHistoryNormalize";
import { getListFromApiResponse } from "../../utils/apiListMappers";
import {
  buildSaasModuleListQueryParams,
  buildSaasBillingExportFilters,
  getBillingExportFileUrlFromResponse,
  getBillingExportMessageFromResponse,
  getBillingExportIdFromResponse,
  getBillingExportFileNameFromResponse,
  isBillingExportResponseSuccess,
} from "../../../utils/buildSaasBillingExportFilters";
import { downloadBillingExportFile } from "../../../utils/downloadBillingExportFile";
import { showSuccess, showError } from "../../../utils/toast";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

const saasModuleList = MockResponses.saasModules?.result ?? [];
const moduleIdToName = Object.fromEntries(
  (saasModuleList || [])
    .map((m) => {
      const id = m.id != null ? String(m.id) : "";
      return id ? [id, String(m.name ?? m.moduleName ?? id).trim() || id] : null;
    })
    .filter(Boolean)
);
/** Prefer name fields from GET /billing/saas-module rows; fall back to catalog or id. */
const getModuleDisplayName = (row) => {
  if (!row || typeof row !== "object") return "N/A";
  const fromRow =
    row.moduleName ??
    row.module_name ??
    (typeof row.module === "object" && row.module != null
      ? (row.module.name ?? row.module.moduleName ?? row.module.title)
      : null) ??
    (typeof row.module === "string" ? row.module : null);
  const direct = fromRow != null ? String(fromRow).trim() : "";
  if (direct) return direct;
  const moduleId = row.moduleId ?? row.module_id ?? row.module?.id;
  const idKey = moduleId != null ? String(moduleId).trim() : "";
  if (idKey && moduleIdToName[idKey] != null) return moduleIdToName[idKey];
  if (idKey) return idKey;
  return "N/A";
};

/** Align GET /billing/saas-module values with filter labels: Both | Monthly | Implementation */
const BILLING_TYPE_FROM_API = {
  BOTH: "Both",
  MONTHLY: "Monthly",
  IMPLEMENTATION: "Implementation",
  IMPLEMENTATION_ONLY: "Implementation",
  RECURRING: "Monthly",
  MRR: "Monthly",
  ONE_TIME: "Implementation",
  ONE_OFF: "Implementation",
};

const normalizeBillingTypeForListRow = (row) => {
  if (!row || typeof row !== "object") return "";
  const candidates = [
    row.billingType,
    row.billing_type,
    row.billingTypeName,
    row.billing_type_name,
  ];
  let s = "";
  for (const v of candidates) {
    if (v != null && String(v).trim() !== "") {
      s = String(v).trim();
      break;
    }
  }
  if (!s) return "";
  const enumKey = s.toUpperCase().replace(/[\s-]+/g, "_");
  if (BILLING_TYPE_FROM_API[enumKey]) return BILLING_TYPE_FROM_API[enumKey];
  const lower = s.toLowerCase();
  if (lower === "both") return "Both";
  if (lower === "monthly") return "Monthly";
  if (lower === "implementation") return "Implementation";
  return s;
};

function resolveAdditionalFeeRaw(row) {
  if (!row || typeof row !== "object") return "";
  const raw = row.additionalFee ?? row.additional_fee;
  if (raw == null || raw === "") return "";
  return raw;
}

/** True when API sent a dollar amount (number or numeric string). */
function isNumericAdditionalFee(value) {
  if (value == null || value === "") return false;
  if (typeof value === "number" && Number.isFinite(value)) return true;
  const s = String(value).trim();
  return s !== "" && /^-?\d+(\.\d+)?$/.test(s);
}

/** Show notes text as-is; numeric values as currency (legacy rows). */
function formatAdditionalFeeForDisplay(row) {
  const raw = resolveAdditionalFeeRaw(row);
  if (raw === "" || raw == null) return "—";
  if (isNumericAdditionalFee(raw)) {
    return `$${Number(raw).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
  }
  return String(raw).trim();
}

const DEFAULT_FILTERS = {
  client: "All Clients",
  /** Module catalog id; empty = all modules */
  module: "",
  status: "Active",
  billingType: "Both",
  dateRange: { startDate: null, endDate: null },
};

const SaasBilling = () => {
  const dispatch = useDispatch();
  const listData = useSelector(selectSaasBillingList) || [];
  const loading = useSelector(selectSaasBillingLoading);
  const error = useSelector(selectSaasBillingError);
  const clientsData = useSelector(selectClientsData);
  const clientsList = useMemo(() => getListFromApiResponse(clientsData), [clientsData]);
  const exportHistoryData = useSelector(selectExportsHistoryData) || [];

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState("detailed");
  const [currentPage, setCurrentPage] = useState(1);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exportSubmitting, setExportSubmitting] = useState(false);
  const [historyDownloadingId, setHistoryDownloadingId] = useState(null);
  const exportDropdownRef = useRef(null);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const { can } = usePermissions();
  const canRunSaasExport = can(PERMISSION_KEYS.SAAS_BILLING_EXPORT);
  const itemsPerPage = 10;

  const guardSaasExportRun = () => {
    if (!can(PERMISSION_KEYS.SAAS_BILLING_EXPORT)) {
      setDeniedPermissionKey(PERMISSION_KEYS.SAAS_BILLING_EXPORT);
      return false;
    }
    return true;
  };

  /** Keep latest rows for clientId fallback without re-creating the fetch callback on every list response. */
  const listDataRef = useRef(listData);
  listDataRef.current = listData;

  const refetchSaasList = useCallback(
    (uiFilters) => {
      const q = buildSaasModuleListQueryParams(uiFilters, {
        clientsList,
        listData: listDataRef.current,
        moduleCatalog: saasModuleList,
      });
      dispatch(fetchSaasBillingList(q));
    },
    [dispatch, clientsList]
  );

  useEffect(() => {
    dispatch(fetchClientsData({ getAll: true }));
    dispatch(fetchExportsHistory({ type: "saas" }));
    const q = buildSaasModuleListQueryParams(DEFAULT_FILTERS, {
      clientsList: [],
      listData: [],
      moduleCatalog: saasModuleList,
    });
    dispatch(fetchSaasBillingList(q));
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleDateRangeChange = (dateRange) => {
    setFilters((prev) => ({ ...prev, dateRange }));
  };

  const handleFilterReset = (defaultFilters) => {
    const next = defaultFilters || DEFAULT_FILTERS;
    setFilters(next);
    setCurrentPage(1);
    refetchSaasList(next);
  };

  const handleApplyFilters = useCallback(() => {
    setCurrentPage(1);
    refetchSaasList(filters);
  }, [filters, refetchSaasList]);

  const clientOptions = useMemo(() => {
    const fromApi = clientsList
      .map((c) => String(c.clientName ?? c.name ?? "").trim())
      .filter(Boolean);
    const fromRows = listData.map((row) => row.clientName).filter(Boolean);
    const names = Array.from(new Set([...fromApi, ...fromRows])).sort();
    return ["All Clients", ...names];
  }, [clientsList, listData]);

  const moduleOptions = useMemo(() => {
    const rows = (saasModuleList || [])
      .map((m) => ({
        value: String(m.id ?? "").trim(),
        label: String(m.name ?? m.id ?? "").trim() || String(m.id),
      }))
      .filter((o) => o.value);
    rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    return [{ value: "", label: "All Modules" }, ...rows];
  }, []);

  const sortedDataWithDisplayFields = useMemo(
    () =>
      listData.map((row) => ({
        ...row,
        billingType: normalizeBillingTypeForListRow(row),
        _moduleName: getModuleDisplayName(row),
      })),
    [listData]
  );

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(
    sortedDataWithDisplayFields,
    {
      defaultSortKey: "clientName",
      defaultSortDirection: "asc",
    }
  );

  const totalImplementation = sortedData.reduce(
    (sum, row) => sum + (row.implementationFee || 0),
    0
  );
  const totalMonthly = sortedData.reduce((sum, row) => sum + (row.monthlyFee || 0), 0);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleHistoryDownload = async (row) => {
    if (!row?.canDownload || !row?.id) return;
    setHistoryDownloadingId(row.id);
    try {
      const ext = (row.format || "CSV").toLowerCase();
      const fallback = row.fileName?.trim() || `export.${ext === "xlsx" ? "xlsx" : "csv"}`;
      await downloadBillingExportFile(row.id, fallback);
    } catch (e) {
      showError(getApiErrorMessage(e, "Download failed"));
    } finally {
      setHistoryDownloadingId(null);
    }
  };

  const handleSaasExport = async (format) => {
    if (!guardSaasExportRun()) return;
    setExportSubmitting(true);
    setExportDropdownOpen(false);
    try {
      const filterPayload = buildSaasBillingExportFilters(
        filters,
        listData,
        saasModuleList,
        clientsList
      );
      const result = await dispatch(
        createBillingExport({ format, reportType: "saas", filters: filterPayload })
      ).unwrap();
      if (!isBillingExportResponseSuccess(result)) {
        showError(getBillingExportMessageFromResponse(result) || "Export failed");
        return;
      }
      const exportId = getBillingExportIdFromResponse(result);
      const suggestedName = getBillingExportFileNameFromResponse(result);
      const url = getBillingExportFileUrlFromResponse(result);
      const msg = getBillingExportMessageFromResponse(result);
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const fallbackName = suggestedName || `export.${ext}`;

      if (exportId) {
        await downloadBillingExportFile(exportId, fallbackName);
        showSuccess(msg || "Export downloaded");
      } else if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        showSuccess(msg || "Export ready");
      } else {
        showSuccess(msg || "Export submitted");
      }
      dispatch(fetchExportsHistory({ type: "saas" }));
    } catch (e) {
      showError(typeof e === "string" ? e : getApiErrorMessage(e, "Export failed"));
    } finally {
      setExportSubmitting(false);
    }
  };

  return (
    <>
      {exportSubmitting && <Loader />}
      <ContentLoader loading={loading} className="min-h-[50vh]">
        <div className="clients-page-content !bg-transparent w-full">
          <Typography variant="h4" className="mt-3 mb-4">
            SaaS Module Billing
          </Typography>

          <SaasBillingFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onDateRangeChange={handleDateRangeChange}
            onFilterReset={handleFilterReset}
            onApplyFilters={handleApplyFilters}
            clientOptions={clientOptions}
            moduleOptions={moduleOptions}
          />

          {canRunSaasExport ? (
            <div className="flex justify-end mt-2 mb-4">
              <div className="relative" ref={exportDropdownRef}>
                <Button
                  variant="neutral"
                  size="vsm"
                  title="Export"
                  className="btn-reset square-btn"
                  data-tooltipp="Export"
                  disabled={exportSubmitting}
                  onClick={() => {
                    if (!guardSaasExportRun()) return;
                    setExportDropdownOpen(!exportDropdownOpen);
                  }}
                >
                  <RiUpload2Line className="text-lg text-text-secondary" />
                </Button>
                {exportDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <Button
                      type="button"
                      disabled={exportSubmitting}
                      onClick={() => {
                        void handleSaasExport("csv");
                      }}
                      className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                    >
                      <Typography variant="text2" component="span">
                        Export CSV
                      </Typography>
                    </Button>
                    <Button
                      type="button"
                      disabled={exportSubmitting}
                      onClick={() => {
                        void handleSaasExport("xlsx");
                      }}
                      className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                    >
                      <Typography variant="text2" component="span">
                        Export XLSX
                      </Typography>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* SaaS Summary totals - same format as ExportPreview */}

          {/* SaaS Summary Table - same structure as ClientList */}
          <div className="w-full mb-4">
            <Typography variant="h4" className="mb-3 ms-1">
              SaaS Summary
            </Typography>
            <div className=" max-w-[100%] bg-surface-primary  px-3 py-2 flex justify-between items-center">
              <Typography variant="h5" className="text-text-secondary m-0">
                Total Implementation Fees
              </Typography>
              <Typography variant="span1" className="font-semibold text-lg m-0">
                ${totalImplementation.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </div>
            <div className="mb-4 max-w-[100%] bg-surface-primary  px-3 py-2 flex m-0 justify-between items-center">
              <Typography variant="h5" className="text-text-secondary  m-0">
                Total Monthly Recurring
              </Typography>
              <Typography variant="span1" className="font-semibold text-lg m-0">
                ${totalMonthly.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </div>

            <div className="w-full">
              <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow mt-4">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full mb-0 clients-table">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <SortableHeader
                              columnKey="clientName"
                              label="Client"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="_moduleName"
                              label="Module"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="implementationFee"
                              label="Implementation Fee"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              className="text-right"
                            />
                            <SortableHeader
                              columnKey="monthlyFee"
                              label="Monthly Fee"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              className="text-right"
                            />
                            <SortableHeader
                              columnKey="additionalFee"
                              label="Additional info"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              className="text-start"
                            />
                            <SortableHeader
                              columnKey="billingType"
                              label="Billing Type"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="startDate"
                              label="Start Date"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="endDate"
                              label="End Date"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="status"
                              label="Status"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              sortable={false}
                            />
                          </tr>
                        </thead>
                        <tbody className={`relative ${loading ? "h-300" : ""}`}>
                          {loading ? (
                            <tr>
                              <td colSpan={9} className="text-center">
                                <Loader />
                              </td>
                            </tr>
                          ) : error ? (
                            <tr>
                              <td colSpan={9} className="text-center py-6">
                                <Typography variant="span1" className="text-badge-danger">
                                  {error}
                                </Typography>
                              </td>
                            </tr>
                          ) : paginatedData.length > 0 ? (
                            paginatedData.map((row) => {
                              const additionalFeeDisplay = formatAdditionalFeeForDisplay(row);
                              return (
                                <tr key={row.id}>
                                  <td className="text-start align-middle">
                                    <Typography variant="span1" className="font-medium">
                                      {row.clientName || "N/A"}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle">
                                    <Typography variant="span1">
                                      {row._moduleName || "N/A"}
                                    </Typography>
                                  </td>
                                  <td className="text-end align-middle">
                                    <Typography variant="span1">
                                      $
                                      {Number(row.implementationFee || 0).toLocaleString(
                                        undefined,
                                        {
                                          minimumFractionDigits: 0,
                                        }
                                      )}
                                    </Typography>
                                  </td>
                                  <td className="text-end align-middle">
                                    <Typography variant="span1">
                                      $
                                      {Number(row.monthlyFee || 0).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                      })}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle max-w-[14rem]">
                                    <Typography
                                      variant="span1"
                                      className="block truncate"
                                      title={
                                        additionalFeeDisplay === "—"
                                          ? undefined
                                          : additionalFeeDisplay
                                      }
                                    >
                                      {additionalFeeDisplay}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle">
                                    <Typography variant="span1">
                                      {row.billingType || "N/A"}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle">
                                    <Typography variant="span1">
                                      {formatDate(row.startDate, { emptyLabel: "—" })}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle">
                                    <Typography variant="span1">
                                      {formatDate(row.endDate, { emptyLabel: "—" })}
                                    </Typography>
                                  </td>
                                  <td className="text-start align-middle">
                                    <Typography
                                      variant="span1"
                                      className="badge rounded-full px-3 py-1 text-white text-xs font-bold bg-badge-success"
                                    >
                                      {row.status || "Active"}
                                    </Typography>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={9} className="text-center">
                                <NoDataFound message="No SaaS module billing data found" />
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="bg-surface-secondary border-t-2 border-border-primary">
                          <tr>
                            <td
                              colSpan={2}
                              className="text-start align-middle px-3 py-2 text-sm font-semibold text-text-primary"
                            >
                              Total
                            </td>
                            <td className="text-end align-middle px-3 py-2 text-sm font-semibold text-text-primary">
                              $
                              {totalImplementation.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td className="text-end align-middle px-3 py-2 text-sm font-semibold text-text-primary">
                              $
                              {totalMonthly.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2" />
                            <td colSpan={4} className="px-3 py-2" />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="mt-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>

          {/* Export History - same table styling as ClientList */}
          <div className="w-full mt-8">
            <Typography variant="h4" className="mb-3">
              Export History
            </Typography>
            <div className="w-full">
              <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full mb-0 clients-table">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <th className="text-start align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Export Date
                            </th>
                            <th className="text-start align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Exported By
                            </th>
                            <th className="text-start align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Filters
                            </th>
                            <th className="text-start align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Format
                            </th>
                            <th className="text-start align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Status
                            </th>
                            <th className="text-start align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Rows
                            </th>
                            <th className="text-end align-middle px-3 py-2 text-sm font-medium text-text-primary border-b border-border-primary">
                              Download
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {exportHistoryData.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center">
                                <NoDataFound message="No export history found" />
                              </td>
                            </tr>
                          ) : (
                            exportHistoryData.map((row) => (
                              <tr key={row.id}>
                                <td className="text-start align-middle">
                                  <Typography variant="span1" className="font-medium">
                                    {row.date}
                                  </Typography>
                                </td>
                                <td className="text-start align-middle">
                                  <Typography variant="span1">{row.exportedBy}</Typography>
                                </td>
                                <td className="text-start align-middle">
                                  <Typography variant="span1" className="text-text-secondary">
                                    {row.filters}
                                  </Typography>
                                </td>
                                <td className="text-start align-middle">
                                  <Typography variant="span1">{row.format || "—"}</Typography>
                                </td>
                                <td className="text-start align-middle">
                                  <Typography
                                    variant="span1"
                                    className={
                                      row.status === "success"
                                        ? "text-text-primary"
                                        : "text-text-secondary"
                                    }
                                    title={row.errorMessage || undefined}
                                  >
                                    {formatExportStatusLabel(row.status)}
                                  </Typography>
                                </td>
                                <td className="text-start align-middle">
                                  <Typography variant="span1">{row.rowCount}</Typography>
                                </td>
                                <td className="text-end align-middle">
                                  {row.canDownload ? (
                                    <Typography
                                      variant="span1"
                                      component="button"
                                      type="button"
                                      disabled={historyDownloadingId === row.id}
                                      className="cursor-pointer underline hover:opacity-80 focus:outline-none text-text-link bg-transparent border-0 p-0 font-inherit disabled:opacity-50 disabled:cursor-not-allowed"
                                      onClick={() => void handleHistoryDownload(row)}
                                    >
                                      {historyDownloadingId === row.id
                                        ? "Downloading…"
                                        : "Download"}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="span1"
                                      className="text-text-secondary"
                                      title={row.errorMessage || "Export file not available"}
                                    >
                                      —
                                    </Typography>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PermissionDeniedModal
            isOpen={deniedPermissionKey != null}
            onClose={() => setDeniedPermissionKey(null)}
            permissionKey={deniedPermissionKey}
          />
        </div>
      </ContentLoader>
    </>
  );
};

export default SaasBilling;
